import React, { createContext, useContext, useMemo, useState } from 'react';
import { endOfToday, startOfToday, subDays } from 'date-fns';
import { MandatoryChildren } from '../../models/children-props';
import { SocialProfileFieldsFragment, useGetSocialProfilesQuery } from '../../models/graphql';
import { AppContext } from '../../providers/AppProvider';
import { AnalyticsVisualizationType } from './AnalyticsDashboard/AnalyticsDashboard.controller';
import { profilesHelpers } from '../Settings/Profiles/Profiles.helpers';

const ALLOWED_PROFILES = [
  'FacebookSocialProfile',
  'InstagramSocialProfile',
  'LinkedInSocialProfile',
  'PinterestSocialProfile',
  'TwitterSocialProfile',
  'GenericSocialProfile'
];

export type SocialProfilesAnalytics = SocialProfileFieldsFragment;
type AnalyticsContextType = {
  setView: (view: AnalyticsVisualizationType) => void;
  view: AnalyticsVisualizationType | undefined;
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  socialProfiles: SocialProfilesAnalytics[];
  selectedProfilesIds: string[];
  setSelectedProfileIds: (ids: string[]) => void;
};
export const AnalyticsContext = createContext<AnalyticsContextType>({
  startDate: new Date(),
  endDate: new Date(),
  setStartDate: _ => {
    /* void */
  },
  setEndDate: _ => {
    /* void */
  },
  socialProfiles: [],
  selectedProfilesIds: [],
  setSelectedProfileIds: _ => {
    /* void */
  },
  view: undefined,
  setView: () => {
    /* void */
  }
});
type AnalyticsProviderProps = MandatoryChildren;
export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const { data: profilesData } = useGetSocialProfilesQuery({
    variables: { workspaceId: selectedWorkspaceId ?? '' }
  });

  const [startDate, setStartDate] = useState<Date>(subDays(startOfToday(), 30));
  const [endDate, setEndDate] = useState<Date>(endOfToday());
  const [selectedProfilesIds, setSelectedProfileIds] = useState<string[]>([]);

  // maps and filters all profiles, transforming Generic profiles to their corresponding allowed profile types
  const socialProfiles = useMemo(
    () =>
      profilesData?.workspace.socialProfiles
        .map(profile => {
          if (!profile.__typename) return null;

          if (profile.__typename === 'GenericSocialProfile') {
            const mappedType = profilesHelpers.mapGenericProfileToTypename(profile.genericProfile);
            if (mappedType && ALLOWED_PROFILES.includes(mappedType)) {
              return { ...profile, __typename: mappedType };
            }
            return null; // exclude if mapping is invalid
          }

          return ALLOWED_PROFILES.includes(profile.__typename) ? profile : null;
        })
        .filter((profile): profile is SocialProfileFieldsFragment => profile !== null) ?? [], // filter null values
    [profilesData]
  );

  const [view, setView] = useState<AnalyticsVisualizationType>(AnalyticsVisualizationType.overview);

  const contextValue = useMemo(() => ({
    view,
    setView,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    socialProfiles,
    selectedProfilesIds,
    setSelectedProfileIds
  }), [endDate, selectedProfilesIds, socialProfiles, startDate, view]) ;
  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};
